import {
  DrawerDescription,
  FormSwitchGroup,
  DrawerContent,
  DrawerTrigger,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormSelect,
  FormInput,
  Loading,
  Drawer,
  Button,
  Label,
  Form,
} from '@ui/components';
import { PropsWithChildren, useState, FC } from 'react';
import { Cancel } from '@ui/components/icons';

import { useUserInvite } from './use-user-invite';

export const UserInviteSidebar: FC<PropsWithChildren> = ({ children }) => {
  const [open, onOpenChange] = useState<boolean>(false);
  const {
    isCreateUserPending,
    roleOptions,
    form,
    t,
    onSubmit,
    isCostCentersFetching,
    costCentersOptions,
  } = useUserInvite(() => onOpenChange(false));

  return (
    <Drawer onClose={() => form.reset()} onOpenChange={onOpenChange} open={open} handleOnly>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle asChild>
            <Typography variant="h200">{t('USERS.INVITE_USER_SIDEBAR_HEADER')}</Typography>
          </DrawerTitle>
          <DrawerDescription />
          <DrawerClose asChild>
            <Button variant="ghost" size="small" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <form
          className="flex-1 overflow-auto flex flex-col p-4 pt-5 gap-2"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Loading isLoading={isCostCentersFetching}>
            <div className="flex-1">
              <div className="space-y-5 border-b border-border-weak pb-4">
                <Typography variant="labelMediumBold">
                  {t('USERS.INVITE_USER_SIDEBAR_FORM_SECTION_LABEL')}
                </Typography>

                <Form {...form}>
                  <div className="grid grid-cols-6 gap-2 items-center">
                    <Label className="col-span-2" htmlFor="firstName">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('USERS.FIRST_NAME')}
                      </Typography>
                    </Label>
                    <FormInput
                      componentProps={{
                        id: 'firstName',
                      }}
                      placeholder={t('USERS.FIRST_NAME')}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="firstName"
                      size="small"
                      required
                    />

                    <Label className="col-span-2" htmlFor="lastName">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('USERS.LAST_NAME')}
                      </Typography>
                    </Label>
                    <FormInput
                      componentProps={{
                        id: 'lastName',
                      }}
                      placeholder={t('USERS.LAST_NAME')}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="lastName"
                      size="small"
                      required
                    />
                    <Label className="col-span-2" htmlFor="email">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('USERS.EMAIL')}
                      </Typography>
                    </Label>
                    <FormInput
                      componentProps={{
                        id: 'email',
                      }}
                      placeholder={t('USERS.EMAIL')}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="email"
                      size="small"
                      required
                    />

                    <Label className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('USERS.ROLE')}
                      </Typography>
                    </Label>
                    <FormSelect
                      placeholder={t('USERS.ROLE')}
                      formControl={form.control}
                      className="col-span-4"
                      options={roleOptions}
                      name="role"
                    />
                  </div>
                </Form>
              </div>
              {costCentersOptions.length > 1 && (
                <div className="space-y-6 mt-5">
                  <Typography variant="labelMediumBold">
                    {t('USERS.INVITE_USER_SIDEBAR_COST_CENTER_TITLE')}
                  </Typography>
                  <Form {...form}>
                    <FormSwitchGroup
                      switchClassName="col-span-1 justify-self-end"
                      className="grid grid-cols-6 gap-4"
                      options={costCentersOptions}
                      labelClassName="col-span-5"
                      control={form.control}
                      name="costCenters"
                    />
                  </Form>
                </div>
              )}
            </div>
          </Loading>
        </form>
        <DrawerFooter>
          <Button
            isLoading={isCreateUserPending || isCostCentersFetching}
            disabled={isCreateUserPending || isCostCentersFetching}
            onClick={form.handleSubmit(onSubmit)}
            className="shrink-0"
            type="submit"
            size="large"
            wide
          >
            {t('USERS.INVITE_USER_SIDEBAR_SUBMIT')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
